import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { ensureCaseForBooking } from "@/lib/clinic.functions";
import { mapAdminBookings } from "@/lib/admin-bookings-map";

async function assertStaff(supabase: any, userId: string) {
  const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId);
  const roles = (data ?? []).map((r: { role: string }) => r.role);
  if (!roles.includes("admin") && !roles.includes("assistant")) throw new Error("Forbidden");
  return { isAdmin: roles.includes("admin") };
}

import { assertAdmin } from "@/lib/admin-check";

// Staff full-column reads must bypass the column-level GRANT restrictions on
// bookings/sessions (admin_notes, payment_notes, session_notes, private_notes).
// The `authenticated` role only has SELECT on client-safe columns; service_role
// keeps ALL. Call this only inside a staff-gated server fn.
async function admin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

// -------- Bookings --------
export const listAdminBookings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: { showTest?: boolean } | undefined) =>
    z.object({ showTest: z.boolean().default(false) }).parse(d ?? {}),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase, context.userId);
    const sb = await admin();
    let q = sb
      .from("bookings")
      .select("id, status, payment_status, scheduled_at, meeting_link, created_at, service_id, client_id, receipt_file, admin_notes, payment_notes, receipt_rejection_reason, case_id, session_mode, payment_method, payment_plan, preferred_times")
      .is("deleted_at", null)
      .order("created_at", { ascending: false });
    if (!data.showTest) q = q.eq("is_test", false);
    const [bookingsRes, servicesRes, profilesRes] = await Promise.all([
      q,
      sb.from("services").select("id, title_ar, slug, price_usd, duration_min"),
      sb.from("profiles").select("id, user_id, full_name, phone_whatsapp, country"),
    ]);
    if (bookingsRes.error) throw bookingsRes.error;
    return mapAdminBookings(
      (bookingsRes.data ?? []) as any,
      (servicesRes.data ?? []) as any,
      (profilesRes.data ?? []) as any,
    );
  });

export const getAdminBooking = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase, context.userId);
    const sb = await admin();
    const { data: b, error } = await sb
      .from("bookings")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw error;
    if (!b) return null;
    const [svc, prof, intake] = await Promise.all([
      sb.from("services").select("title_ar, slug, price_usd, duration_min").eq("id", b.service_id).maybeSingle(),
      sb.from("profiles").select("full_name, phone_whatsapp, country, city, user_id").eq("id", b.client_id).maybeSingle(),
      b.intake_submission_id
        ? sb.from("form_submissions").select("answers, form_snapshot").eq("id", b.intake_submission_id).maybeSingle()
        : Promise.resolve({ data: null as any }),
    ]);
    let receiptUrl: string | null = null;
    // Only sign receipts stored in the booking owner's own folder for this booking.
    const ownerId = (prof.data as any)?.user_id as string | undefined;
    if (b.receipt_file && ownerId && b.receipt_file.startsWith(`${ownerId}/${b.id}-`)) {
      const { data: signed } = await sb.storage
        .from("payment-receipts")
        .createSignedUrl(b.receipt_file, 60 * 30);
      receiptUrl = signed?.signedUrl ?? null;
    }
    let intakeSchema: any = intake.data?.form_snapshot ?? null;
    if (!intakeSchema) {
      const { data: f } = await sb.from("forms").select("schema").eq("slug", "consultation-intake").maybeSingle();
      intakeSchema = f?.schema ?? null;
    }
    const { user_id: _omit, ...clientData } = (prof.data ?? {}) as any;
    return {
      booking: b,
      service: svc.data,
      client: prof.data ? clientData : null,
      intakeAnswers: intake.data?.answers ?? null,
      intakeSchema,
      receiptUrl,
    };
  });

const bookingStatusEnum = z.enum(["new","contacted","awaiting_payment","confirmed","completed","cancelled","no_show"]);

export const updateBookingStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: { id: string; status: string }) =>
    z.object({ id: z.string().uuid(), status: bookingStatusEnum }).parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase, context.userId);
    const sb = await admin();
    const { data: before } = await sb.from("bookings").select("status, slot_id").eq("id", data.id).maybeSingle();
    const { error } = await sb.from("bookings").update({ status: data.status }).eq("id", data.id);
    if (error) throw error;
    // Release the slot when a booking is cancelled so it becomes bookable again.
    if (data.status === "cancelled" && before?.slot_id) {
      await sb
        .from("availability_slots")
        .update({ status: "open", booking_id: null })
        .eq("id", before.slot_id);
    }
    return { ok: true };
  });

export const verifyBookingPayment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.supabase, context.userId);
    const sb = await admin();
    const { error } = await sb
      .from("bookings")
      .update({ payment_status: "verified", status: "confirmed", receipt_rejection_reason: null })
      .eq("id", data.id);
    if (error) throw error;
    // Phase 1: guarantee a case exists and is linked. Never fail verification.
    try {
      await ensureCaseForBooking(data.id);
    } catch (e: any) {
    }
    return { ok: true };
  });

export const rejectBookingReceipt = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: { id: string; reason: string; admin_note?: string }) =>
    z.object({
      id: z.string().uuid(),
      reason: z.string().min(2).max(500),
      admin_note: z.string().max(2000).optional().nullable(),
    }).parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertAdmin(context.supabase, context.userId);
    const sb = await admin();
    const { data: prev } = await sb.from("bookings").select("payment_notes").eq("id", data.id).maybeSingle();
    const stamped = `[${new Date().toISOString()}] رفض الإيصال: ${data.reason}${data.admin_note ? ` — ${data.admin_note}` : ""}`;
    const nextNotes = [prev?.payment_notes, stamped].filter(Boolean).join("\n\n");
    const { error } = await sb
      .from("bookings")
      .update({
        payment_status: "unpaid",
        status: "awaiting_payment",
        receipt_rejection_reason: data.reason,
        payment_notes: nextNotes,
      })
      .eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

export const updateBookingDetails = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: { id: string; scheduled_at?: string | null; meeting_link?: string | null; admin_notes?: string | null; payment_notes?: string | null }) =>
    z.object({
      id: z.string().uuid(),
      scheduled_at: z.string().nullable().optional(),
      meeting_link: z.string().nullable().optional(),
      admin_notes: z.string().nullable().optional(),
      payment_notes: z.string().nullable().optional(),
    }).parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase, context.userId);
    const sb = await admin();
    const patch: any = {};
    if (data.scheduled_at !== undefined) patch.scheduled_at = data.scheduled_at;
    if (data.meeting_link !== undefined) patch.meeting_link = data.meeting_link;
    if (data.admin_notes !== undefined) patch.admin_notes = data.admin_notes;
    if (data.payment_notes !== undefined) patch.payment_notes = data.payment_notes;
    const { error } = await sb.from("bookings").update(patch).eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

// -------- Blog --------
export const listAdminPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertStaff(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("posts")
      .select("id, slug, title_ar, status, published_at, views_count, updated_at, cover_image, tags")
      .is("deleted_at", null)
      .order("updated_at", { ascending: false });
    if (error) throw error;
    return data ?? [];
  });

export const getAdminPost = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase, context.userId);
    const { data: post, error } = await context.supabase
      .from("posts")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw error;
    return post;
  });

const upsertPostInput = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().min(2).regex(/^[a-z0-9-]+$/),
  title_ar: z.string().min(2),
  excerpt_ar: z.string().optional().default(""),
  seo_title: z.string().optional().nullable(),
  body_md: z.string().default(""),
  cover_image: z.string().url().optional().nullable(),
  tags: z.array(z.string()).default([]),
  reading_min: z.number().int().min(1).max(60).default(5),
  status: z.enum(["draft", "published"]).default("draft"),
  // ISO datetime. The blog is written weeks ahead: a published post whose
  // `published_at` is in the future stays hidden from every public read path
  // (see `onlyLivePosts`) until that moment arrives.
  published_at: z.string().datetime().optional().nullable(),
});

export const upsertPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => upsertPostInput.parse(d))
  .handler(async ({ context, data }) => {
    const { isAdmin } = await assertStaff(context.supabase, context.userId);
    if (!isAdmin) throw new Error("Forbidden");
    const now = new Date().toISOString();
    const patch: any = {
      slug: data.slug,
      title_ar: data.title_ar,
    excerpt_ar: data.excerpt_ar,
    seo_title: data.seo_title || null,
    body_md: data.body_md,
      cover_image: data.cover_image ?? null,
      tags: data.tags,
      reading_min: data.reading_min,
      status: data.status,
      author_id: context.userId,
    };
    // Never silently reset an editorial schedule: an explicit date wins, and
    // `now` is only used when publishing a post that has no date at all.
    if (data.published_at) {
      patch.published_at = data.published_at;
    } else if (data.status === "published") {
      if (data.id) {
        const { data: existing } = await context.supabase
          .from("posts")
          .select("published_at")
          .eq("id", data.id)
          .maybeSingle();
        if (!existing?.published_at) patch.published_at = now;
      } else {
        patch.published_at = now;
      }
    }
    if (data.id) {
      const { error } = await context.supabase.from("posts").update(patch).eq("id", data.id);
      if (error) throw error;
      return { id: data.id };
    } else {
      const { data: row, error } = await context.supabase.from("posts").insert(patch).select("id").single();
      if (error) throw error;
      return { id: row.id };
    }
  });

export const softDeletePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    const { isAdmin } = await assertStaff(context.supabase, context.userId);
    if (!isAdmin) throw new Error("Forbidden");
    const { error } = await context.supabase.from("posts").update({ deleted_at: new Date().toISOString() }).eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });
